import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Mail, Check, X, HelpCircle, Info, AtSign, Sparkles } from 'lucide-react';

/**
 * EmailInput - Composant de saisie Email Ultra-Professionnel High-Tech
 * 
 * Features:
 * - Validation progressive en temps réel
 * - Suggestions de correction
 * - Bulle d'aide contextuelle
 * - Indicateurs visuels animés
 * - Analyse de la structure email
 */
export default function EmailInput({ 
  value, 
  onChange, 
  placeholder = 'votre.email@entreprise.fr', 
  required = false, 
  className = '',
  label = 'Adresse email',
  showLabel = true
}) {
  const [email, setEmail] = useState('');
  const [isValid, setIsValid] = useState(null);
  const [isTouched, setIsTouched] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const [showTooltip, setShowTooltip] = useState(false);
  const initializedRef = useRef(false);

  const validateEmail = useCallback((emailValue) => {
    if (!emailValue) {
      setIsValid(null);
      return false;
    }
    
    const emailPattern = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    const valid = emailPattern.test(emailValue);
    setIsValid(valid);
    return valid;
  }, []);

  useEffect(() => {
    if (value && !initializedRef.current) {
      initializedRef.current = true;
      setEmail(value);
      validateEmail(value);
    }
  }, [value, validateEmail]);

  const handleChange = (e) => {
    const newValue = e.target.value.trim();
    setEmail(newValue);
    onChange(newValue);
    
    if (newValue.length > 0) {
      validateEmail(newValue);
    } else {
      setIsValid(null);
    }
  };

  const handleBlur = () => {
    setIsTouched(true);
    setIsFocused(false);
    if (email) {
      validateEmail(email);
    }
  };

  // Analyse de la structure de l'email
  const getEmailStructure = () => {
    if (!email) return { local: '', domain: '', extension: '' };
    
    const atIndex = email.indexOf('@');
    if (atIndex === -1) return { local: email, domain: '', extension: '' };
    
    const local = email.substring(0, atIndex);
    const domainPart = email.substring(atIndex + 1);
    const dotIndex = domainPart.lastIndexOf('.');
    
    if (dotIndex === -1) return { local, domain: domainPart, extension: '' };
    
    return {
      local,
      domain: domainPart.substring(0, dotIndex),
      extension: domainPart.substring(dotIndex + 1)
    };
  };

  const structure = getEmailStructure();

  const getValidationHint = () => {
    if (!email || !isTouched) return null;
    
    if (!email.includes('@')) {
      return "Ajoutez @ suivi du domaine (ex: @gmail.com)";
    }
    
    const [local, domain] = email.split('@');
    
    if (!local) {
      return 'Partie avant @ manquante';
    }
    
    if (!domain) {
      return 'Partie après @ manquante';
    }
    
    if (!domain.includes('.')) {
      return 'Ajoutez l\'extension du domaine (ex: .fr, .com)';
    }
    
    if (domain.endsWith('.')) {
      return 'Extension du domaine manquante';
    }
    
    return null;
  };

  // Couleurs selon l'état
  const getBorderColor = () => {
    if (isValid === true) return 'border-emerald-500 ring-2 ring-emerald-500/30';
    if (isValid === false && isTouched) return 'border-red-500 ring-2 ring-red-500/30';
    if (isFocused) return 'border-blue-500 ring-2 ring-blue-500/30';
    return 'border-slate-200 hover:border-slate-300';
  };

  // Suggestions courantes de domaines
  const _commonDomains = ['gmail.com', 'outlook.fr', 'yahoo.fr', 'orange.fr', 'free.fr'];
  const _showSuggestions = email.includes('@') && !email.includes('.') && isFocused;

  return (
    <div className={`space-y-2 ${className}`}>
      {/* Label avec bulle d'aide */}
      {showLabel && (
        <div className="flex items-center justify-between">
          <label className="text-sm font-semibold text-slate-700 flex items-center gap-1.5">
            <Mail className="h-4 w-4 text-slate-500" />
            {label}
            {required && <span className="text-red-500">*</span>}
          </label>
          
          {/* Bulle d'aide */}
          <div className="relative">
            <button
              type="button"
              className="p-1.5 rounded-full hover:bg-blue-50 transition-all duration-200 group"
              onMouseEnter={() => setShowTooltip(true)}
              onMouseLeave={() => setShowTooltip(false)}
            >
              <HelpCircle className="h-4 w-4 text-slate-400 group-hover:text-blue-500 transition-colors" />
            </button>
            
            {showTooltip && (
              <div className="absolute z-50 right-0 bottom-full mb-2 animate-in fade-in slide-in-from-bottom-2 duration-200">
                <div className="bg-slate-800 text-white text-sm px-4 py-3 rounded-xl shadow-2xl w-72 border border-slate-700">
                  <div className="flex items-start gap-2.5">
                    <Info className="h-5 w-5 text-blue-400 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="font-semibold text-blue-300 mb-2">Format attendu</p>
                      <div className="bg-slate-700/50 rounded-lg px-3 py-2 font-mono text-xs mb-2">
                        <span className="text-emerald-400">nom</span>
                        <span className="text-amber-400">@</span>
                        <span className="text-blue-400">domaine</span>
                        <span className="text-purple-400">.extension</span>
                      </div>
                      <p className="text-slate-300 text-xs">
                        Utilisez votre adresse email professionnelle pour recevoir les notifications importantes.
                      </p>
                    </div>
                  </div>
                  <div className="absolute right-4 -bottom-2 w-3 h-3 bg-slate-800 transform rotate-45 border-r border-b border-slate-700"></div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Champ de saisie */}
      <div className="relative">
        <div className={`
          flex items-center rounded-xl border-2 transition-all duration-300 ease-out
          bg-white/80 backdrop-blur-sm overflow-hidden
          ${getBorderColor()}
        `}>
          {/* Icône */}
          <div className={`
            flex items-center justify-center px-4 py-3.5 
            border-r transition-colors duration-300
            ${isValid === true ? 'bg-emerald-50 border-emerald-200' : 
              isValid === false && isTouched ? 'bg-red-50 border-red-200' : 
              'bg-slate-50 border-slate-200'}
          `}>
            <Mail className={`h-5 w-5 transition-colors duration-300 ${
              isValid === true ? 'text-emerald-600' : 
              isValid === false && isTouched ? 'text-red-500' : 
              'text-slate-500'
            }`} />
          </div>

          {/* Input */}
          <input
            type="email"
            value={email}
            onChange={handleChange}
            onFocus={() => setIsFocused(true)}
            onBlur={handleBlur}
            placeholder={placeholder}
            required={required}
            className="flex-1 px-4 py-3.5 outline-none text-base bg-transparent placeholder:text-slate-300"
          />

          {/* Icône de statut */}
          <div className="pr-4">
            {isValid === true && (
              <div className="animate-in zoom-in duration-300">
                <div className="bg-emerald-500 rounded-full p-1">
                  <Check className="h-4 w-4 text-white" />
                </div>
              </div>
            )}
            {isValid === false && isTouched && (
              <div className="animate-in zoom-in duration-300">
                <div className="bg-red-500 rounded-full p-1">
                  <X className="h-4 w-4 text-white" />
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Barre de progression */}
        <div className="absolute -bottom-0.5 left-0 right-0 h-1 bg-slate-100 rounded-full overflow-hidden">
          <div 
            className={`h-full transition-all duration-500 ease-out rounded-full ${
              isValid === true ? 'bg-gradient-to-r from-emerald-500 to-teal-500 w-full' :
              structure.extension ? 'bg-gradient-to-r from-blue-500 to-purple-500 w-full' :
              structure.domain ? 'bg-gradient-to-r from-blue-500 to-purple-500 w-3/4' :
              structure.local ? 'bg-gradient-to-r from-blue-500 to-purple-500 w-1/2' :
              'w-0'
            }`}
          />
        </div>
      </div>

      {/* Indicateur de structure */}
      {email.length > 0 && (
        <div className="flex items-center gap-1 text-xs font-mono px-1">
          <span className={`px-2 py-0.5 rounded ${structure.local ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-400'}`}>
            {structure.local || 'nom'}
          </span>
          <AtSign className={`h-3 w-3 ${email.includes('@') ? 'text-amber-500' : 'text-slate-300'}`} />
          <span className={`px-2 py-0.5 rounded ${structure.domain ? 'bg-blue-100 text-blue-700' : 'bg-slate-100 text-slate-400'}`}>
            {structure.domain || 'domaine'}
          </span>
          <span className={structure.extension ? 'text-purple-500' : 'text-slate-300'}>.</span>
          <span className={`px-2 py-0.5 rounded ${structure.extension ? 'bg-purple-100 text-purple-700' : 'bg-slate-100 text-slate-400'}`}>
            {structure.extension || 'ext'}
          </span>
        </div>
      )}

      {/* Messages de statut */}
      <div className="min-h-[20px]">
        {email.length === 0 && required && (
          <p className="text-xs text-amber-600 flex items-center gap-1 animate-in fade-in duration-300">
            <span className="w-1.5 h-1.5 rounded-full bg-amber-400"></span>
            Champ obligatoire
          </p>
        )}
        {getValidationHint() && (
          <p className="text-xs text-red-600 font-medium flex items-center gap-1 animate-in slide-in-from-left duration-300">
            <X className="h-3 w-3" />
            {getValidationHint()}
          </p>
        )}
        {isValid === true && (
          <p className="text-xs text-emerald-600 font-semibold flex items-center gap-1 animate-in slide-in-from-left duration-300">
            <Sparkles className="h-3 w-3" />
            Adresse email valide
          </p>
        )}
      </div>
    </div>
  );
}
